import { SwayyCouponActivityLog } from "./SwayyCoupon";

export interface SwayyCouponActivityDetailRow {
  label: string;
  value: string;
}

const INPUT_LABELS: Record<string, string> = {
  code: "Code",
  description: "Description",
  discount_type: "Discount type",
  discount_value: "Discount value",
  max_discount: "Max discount",
  min_order_value: "Min order value",
  channel: "Channel",
  scope: "Scope",
  scope_ids: "Scope IDs",
  usage_limit: "Usage limit",
  usage_per_user: "Usage per user",
  first_order_only: "First order only",
  starts_at: "Starts at",
  ends_at: "Ends at",
  is_active: "Active",
  is_expired: "Expired",
};

const SKIP_DETAIL_KEYS = new Set(["handler", "input", "error", "bulk"]);

const formatValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value
      .map((item) => (typeof item === "object" ? JSON.stringify(item) : String(item)))
      .join(", ");
  }
  if (typeof value === "string") return value;
  return null;
};

const pushRow = (
  rows: SwayyCouponActivityDetailRow[],
  label: string,
  value: unknown,
) => {
  const formatted = formatValue(value);
  if (!formatted) return;
  if (rows.some((row) => row.label === label)) return;
  rows.push({ label, value: formatted });
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

export const getCouponActivityActorName = (
  log: SwayyCouponActivityLog,
): string => log.admin_name?.trim() || `Admin #${log.admin_id}`;

export const getCouponActivitySummary = (log: SwayyCouponActivityLog): string => {
  const actor = getCouponActivityActorName(log);
  const details = log.details ?? {};
  const code = typeof details.code === "string" ? details.code.trim() : "";
  const error = asRecord(details.error);
  const errorMessage =
    typeof error?.message === "string" ? error.message.trim() : "";

  if (log.status === "failure") {
    return errorMessage ? `${actor} — ${errorMessage}` : `${actor} — failed`;
  }
  if (code) return `${actor} · ${code}`;
  return actor;
};

export const getCouponActivityDetailRows = (
  log: SwayyCouponActivityLog,
): SwayyCouponActivityDetailRow[] => {
  const details = log.details ?? {};
  const rows: SwayyCouponActivityDetailRow[] = [];
  const error = asRecord(details.error);

  pushRow(rows, "Code", details.code);
  if (error) {
    pushRow(rows, "Error", error.message ?? error.code);
    const issues = error.issues;
    if (Array.isArray(issues) && issues.length > 0) {
      const messages = issues
        .map((issue) => {
          const record = asRecord(issue);
          if (!record) return null;
          const path = typeof record.path === "string" ? record.path : "";
          const message = typeof record.message === "string" ? record.message : "";
          if (!message) return null;
          return path ? `${path}: ${message}` : message;
        })
        .filter((item): item is string => Boolean(item));
      pushRow(rows, "Validation", messages);
    }
  }

  const input = asRecord(details.input);
  if (input) {
    Object.entries(INPUT_LABELS).forEach(([key, label]) => {
      pushRow(rows, label, input[key]);
    });
  }

  Object.entries(details).forEach(([key, value]) => {
    if (SKIP_DETAIL_KEYS.has(key) || key === "code") return;
    const label =
      INPUT_LABELS[key] ??
      key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    pushRow(rows, label, value);
  });

  return rows;
};
